import { useNavigate, useOutletContext, useFetcher } from '@remix-run/react'
import {
	AlertTriangleIcon,
	ClipboardIcon,
	CopyIcon,
	CrosshairIcon,
	EditIcon,
	Hexagon,
	MoreVertical,
	ShareIcon,
	StarIcon,
} from 'lucide-react'
import posthog from 'posthog-js'
import { useState } from 'react'
import { toast } from 'sonner'
import { useCopyToClipboard } from 'usehooks-ts'
import { useCancelPlotSubscription } from '#app/hooks/use-cancel-plot-subscription'
import { useResumePlotSubscription } from '#app/hooks/use-resume-plot-subscription'
import { useToggleFavoritePlot } from '#app/hooks/use-toggle-favorite-plot'
import { useToggleFavoritePlotGroup } from '#app/hooks/use-toggle-favorite-plot-group'
import { type LayoutContextType } from '#app/routes/_layout+/_layout'
import { useStoreContext } from '#app/store'
import { cn } from '#app/utils/misc'
import { placeDisplayName } from '#app/utils/place-name'
import { Alert, AlertDescription } from '../ui/alert'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '../ui/alert-dialog'
import { Button, buttonVariants } from '../ui/button'
import { Card } from '../ui/card'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '../ui/dropdown-menu'
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '../ui/tooltip'

export const PlotCard = ({
	type,
	id,
	handle,
	note,
	isExpiring,
	subscriptionId,
	currentPeriodEnd,
	canEdit = false,
	canCancel = false,
	canResume = false,
	groupId,
	user,
}: {
	type: 'plot' | 'group'
	id: string
	note?: string | null
	handle: string
	isExpiring?: boolean
	subscriptionId?: string | null
	currentPeriodEnd?: number
	canEdit?: boolean
	canCancel?: boolean
	canResume?: boolean
	groupId?: string
	user?: {
		id: string
		note?: string | null
		handle: string
		imageUrl: string | null
	}
}) => {
	const iSinglePlot = type === 'plot'

	const highlightedPlotIds = useStoreContext(s => s.highlightedPlotIds)
	const highlightedPlotGroupIds = useStoreContext(
		s => s.highlightedPlotGroupIds,
	)
	const setHighlightedPlotIds = useStoreContext(s => s.setHighlightedPlotIds)
	const setHighlightedPlotGroupIds = useStoreContext(
		s => s.setHighlightedPlotGroupIds,
	)

	const favoritePlotIds = useStoreContext(s => s.favoritePlotIds)
	const favoritePlotGroupIds = useStoreContext(s => s.favoritePlotGroupIds)
	const isFavorite = iSinglePlot
		? favoritePlotIds.includes(id)
		: favoritePlotGroupIds.includes(id)
	const toggleFavoritePlot = useToggleFavoritePlot()
	const toggleFavoritePlotGroup = useToggleFavoritePlotGroup()
	const toggleFavorite = async () => {
		iSinglePlot
			? await toggleFavoritePlot(id)
			: await toggleFavoritePlotGroup(id)
	}

	const resumeSubscription = useResumePlotSubscription(id, subscriptionId)
	const cancelPlotSubscription = useCancelPlotSubscription(id, subscriptionId)

	const clearHighlighted = () => {
		setHighlightedPlotIds([])
		setHighlightedPlotGroupIds([])
	}

	const isHighlighted = iSinglePlot
		? highlightedPlotIds?.includes(id)
		: highlightedPlotGroupIds?.includes(id)
	const setHighlighted = iSinglePlot
		? (ids: string[]) => {
				setHighlightedPlotIds(ids)
				if (groupId) {
					setHighlightedPlotGroupIds([groupId])
				}
			}
		: setHighlightedPlotGroupIds

	const navigate = useNavigate()
	const fetcher = useFetcher()

	const [, copy] = useCopyToClipboard()

	const { flyToPlot, flyToGroupPlot } = useOutletContext<LayoutContextType>()

	const flyTo = iSinglePlot ? flyToPlot : flyToGroupPlot

	const editLink = iSinglePlot
		? `/plot/edit/${handle}`
		: `/plot/edit-group/${handle}`

	// TODO (med) show favorited count
	// TODO (med) show grouped plot count

	const [showCancelDialog, setShowCancelDialog] = useState(false)

	return (
		<div className="flex items-center gap-2">
			<div
				tabIndex={0}
				key={handle}
				className={cn(
					buttonVariants({
						variant: 'unstyled',
					}),
					'w-full text-start',
				)}
				onClick={() => {
					flyTo(id, 'collapseThenExpand')
					navigate(`/plot/${handle}?from=card`)
					clearHighlighted()
					setHighlighted([id])
				}}
			>
				<Card
					className={cn(
						'flex w-full flex-col gap-3 p-2 px-3',
						isHighlighted && 'border-orange-200 bg-orange-50 dark:bg-amber-800',
					)}
				>
					<TooltipProvider>
						<Tooltip>
							{iSinglePlot && (
								<>
									<TooltipTrigger className="absolute right-4 top-4 cursor-default">
										<Hexagon
											className={cn('h-5 w-5 fill-gray-300/50 stroke-gray-400')}
											strokeWidth={1}
										/>
									</TooltipTrigger>
									<TooltipContent align="end">Single place</TooltipContent>
								</>
							)}
							{!iSinglePlot && (
								<>
									<TooltipTrigger className="absolute right-4 top-4 h-5 w-5 cursor-default">
										<div>
											<Hexagon
												className={cn(
													'absolute left-[5px] top-0 h-2.5 w-2.5 fill-gray-300/50 stroke-gray-400',
												)}
												strokeWidth={2}
											/>
											<Hexagon
												className={cn(
													'absolute left-0 top-2 h-2.5 w-2.5 fill-gray-300/50 stroke-gray-400',
												)}
												strokeWidth={2}
											/>
											<Hexagon
												className={cn(
													'absolute left-2.5 top-2 h-2.5 w-2.5 fill-gray-300/50 stroke-gray-400',
												)}
												strokeWidth={2}
											/>
										</div>
									</TooltipTrigger>
									<TooltipContent align="end">Group of places</TooltipContent>
								</>
							)}
						</Tooltip>
					</TooltipProvider>

					<div className="flex flex-col items-start text-left">
						<span className="text-sm">{placeDisplayName(handle, note)}</span>
						{iSinglePlot && subscriptionId === null && (
							<span className="text-xs font-medium text-foreground">
								Free · no renewal
							</span>
						)}
						<Button
							data-vaul-no-drag
							variant="link"
							size="sm"
							className="group text-xs text-muted-foreground"
							onClick={e => {
								e.stopPropagation()
								e.preventDefault()
								copy(`https://erda.app/plot/${handle}`)
								posthog.capture('plot_link_copied', { handle })
								toast.success('Place link copied to clipboard', {
									description: `https://erda.app/plot/${handle}`,
									icon: <ClipboardIcon className="h-3.5 w-3.5" />,
								})
							}}
						>
							erda.app/place/{handle}
							<CopyIcon className="h-3 w-3 opacity-0 group-hover:opacity-100" />
						</Button>
					</div>

					{/* TODO (low) extract alerts */}
					{canResume && isExpiring && currentPeriodEnd && (
						<Alert
							variant="destructive"
							className="-mt-1 border-none bg-red-100 p-2"
						>
							<AlertTriangleIcon className="!top-3 h-4 w-4" />
							<AlertDescription className="mt-1.5 flex items-center gap-3 !pl-8 text-xs">
								Expires on{' '}
								{new Date(currentPeriodEnd * 1000).toLocaleDateString()}
								<Button
									data-vaul-no-drag
									variant="outline"
									size="sm"
									className="h-5 items-center px-1.5 text-xs text-primary"
									onClick={e => {
										e.stopPropagation()
										e.preventDefault()

										resumeSubscription()
									}}
								>
									Resume
								</Button>
							</AlertDescription>
						</Alert>
					)}

					{!canResume && isExpiring && currentPeriodEnd && (
						<Alert
							variant="destructive"
							className="-mt-1 border-none bg-red-100 p-2"
						>
							<AlertTriangleIcon className="!top-3 h-4 w-4 text-red-600" />
							<AlertDescription className="mt-1.5 flex items-center gap-3 !pl-8 text-xs font-semibold text-red-950">
								Gift expires on{' '}
								{new Date(currentPeriodEnd * 1000).toLocaleDateString()}
								<fetcher.Form
									action="/resources/stripe/create-checkout"
									method="post"
									className="inline"
								>
									<Button
										data-vaul-no-drag
										name="plan"
										value={JSON.stringify({
											planId: 'standard',
											planInterval: 'year',
											selectedPlots: [id],
										})}
										type="submit"
										variant="outline"
										size="sm"
										className="h-5 items-center border-red-200 bg-white px-2 text-[10px] font-bold text-red-700 hover:bg-red-50"
										onClick={e => {
											e.stopPropagation()
										}}
									>
										Takeover
									</Button>
								</fetcher.Form>
							</AlertDescription>
						</Alert>
					)}

					<div className="flex flex-wrap items-center gap-2">
						<Button
							data-vaul-no-drag
							variant="outline"
							size="compact"
							className="shadow-none"
							onClick={e => {
								e.stopPropagation()
								e.preventDefault()

								flyTo(id, 'collapseThenExpand')
								clearHighlighted()
								setHighlighted([id])
								posthog.capture('plot_locate', { id })
							}}
						>
							<CrosshairIcon className="h-3.5 w-3.5" /> Locate
						</Button>

						<Button
							data-vaul-no-drag
							variant="outline"
							size="compact"
							className="shadow-none"
							onClick={e => {
								e.stopPropagation()
								e.preventDefault()
								copy(`https://erda.app/plot/${handle}`)
								posthog.capture('plot_link_copied', { handle })
								toast.success('Place link copied to clipboard', {
									description: `https://erda.app/plot/${handle}`,
									icon: <ClipboardIcon className="h-3.5 w-3.5" />,
								})
							}}
						>
							<ShareIcon className="h-3.5 w-3.5" /> Share
						</Button>

						{canEdit && (
							<Button
								data-vaul-no-drag
								variant="outline"
								size="compact"
								className="shadow-none"
								onClick={e => {
									e.stopPropagation()
									e.preventDefault()

									flyTo(id, 'collapseThenExpand')
									navigate(editLink)
								}}
							>
								<EditIcon className="h-3.5 w-3.5" /> Edit
							</Button>
						)}

						{/* TODO (low) redirectTo when not logged in */}
						{!canEdit && (
							<Button
								data-vaul-no-drag
								variant="outline"
								size="compact"
								className="shadow-none"
								onClick={async e => {
									e.stopPropagation()
									toggleFavorite()
								}}
							>
								{isFavorite && (
									<>
										<StarIcon className="h-3.5 w-3.5 fill-yellow-400" /> Remove
									</>
								)}
								{!isFavorite && (
									<>
										<StarIcon className="h-3.5 w-3.5" /> Favorite
									</>
								)}
							</Button>
						)}
					</div>
				</Card>
			</div>
			{canCancel && (
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button
							data-vaul-no-drag
							variant="link"
							size="sm"
							className="h-5 items-center px-1.5"
						>
							<MoreVertical className="h-3.5 w-3.5" />
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align="end">
						<DropdownMenuGroup>
							{isExpiring ? (
								<DropdownMenuItem onClick={resumeSubscription}>
									<span>Resume</span>
								</DropdownMenuItem>
							) : (
								<DropdownMenuItem onClick={() => setShowCancelDialog(true)}>
									<span>Cancel</span>
								</DropdownMenuItem>
							)}
						</DropdownMenuGroup>
					</DropdownMenuContent>
				</DropdownMenu>
			)}
			<AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Are you sure?</AlertDialogTitle>
						<AlertDialogDescription>
							The place will expire{' '}
							{currentPeriodEnd
								? `${new Date(currentPeriodEnd * 1000).toLocaleDateString()}`
								: 'when your subscription ends.'}
							. After that, {user?.handle ?? 'the gift recipient'} will have the
							opportunity to take over the subscription, or it will become
							available for others to claim.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Cancel</AlertDialogCancel>
						<AlertDialogAction onClick={cancelPlotSubscription}>
							Continue
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	)
}
